import { createRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@arabquiz/ui'
import { Loader2, LogIn, Sparkles } from 'lucide-react'
import { Route as rootRoute } from './__root'
import { GameStateProvider, useGameState } from '../game/GameStateProvider'
import { PlayerGameView } from '../game/PlayerGameView'
import {
  clearStoredPlayerSession,
  readStoredPlayerSession,
  type StoredPlayerSession,
} from '../game/playerTokenStorage'

/**
 * `/play` — public, anonymous, PWA-cached entry point for players.
 *
 * Mounts a single `GameStateProvider` at the route root and keeps it alive
 * for the *entire* player journey. The route deliberately never navigates
 * away after a successful join: `PlayerJoinedPayload` does not carry the
 * server-side gameId (it carries a PIN-derived player token instead), so
 * there is no way to construct a `/games/play/$gameId` URL from the
 * client without first plumbing the gameId through the SignalR contracts.
 * Holding the player here also keeps the SignalR connection mounted —
 * navigating away would tear down the websocket and force a Reconnect
 * round-trip on every phase change.
 *
 * Phase routing:
 *   - `idle` → PIN + nickname entry form
 *   - anything else (`lobby` / `question` / `results` / `leaderboard` /
 *     `final` / `error`) → delegate to `<PlayerGameView />`, which
 *     renders the appropriate phase view from the same provider state.
 *
 * This file is the offline shell page referenced by the PWA manifest's
 * `start_url`; the Workbox config in `vite.config.ts` precaches the route
 * via `navigateFallback: '/index.html'` so the entry form is reachable
 * even when the player has no signal until they hit Join.
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play',
  // Players can be deep-linked from the host's "copy join link" affordance
  // with the PIN prefilled as a `pin` search param.
  validateSearch: (search: Record<string, unknown>) => ({
    pin: String(search['pin'] ?? ''),
  }),
  component: PlayRoute,
})

function PlayRoute() {
  return (
    <GameStateProvider>
      <PlayInner />
    </GameStateProvider>
  )
}

function PlayInner() {
  const { state, dispatch, invoke } = useGameState()
  const [storedSession, setStoredSession] = useState<StoredPlayerSession | null>(() =>
    readStoredPlayerSession(),
  )
  const [isReconnecting, setIsReconnecting] = useState(storedSession !== null)
  const reconnectAttemptedRef = useRef(false)

  useEffect(() => {
    if (!storedSession || reconnectAttemptedRef.current) return

    reconnectAttemptedRef.current = true
    dispatch({
      type: 'RESTORE_PLAYER_SESSION',
      gameId: storedSession.gameId,
      token: storedSession.token,
      nickname: storedSession.nickname,
    })

    void invoke('Reconnect', storedSession.gameId, storedSession.token).catch(() => {
      clearStoredPlayerSession()
      setStoredSession(null)
      setIsReconnecting(false)
      dispatch({ type: 'RESET' })
    })
  }, [dispatch, invoke, storedSession])

  useEffect(() => {
    if (!isReconnecting) return

    // Reconnecting to a game that errored OR has already ENDED (`final`) is a
    // dead end: the stored session points at a finished game, so reconnecting
    // would strand the player on the old game's "انتهت اللعبة" screen instead
    // of letting them join a NEW game. In both cases, drop the stale session
    // and fall through to the join form so the player can enter a fresh PIN.
    if (state.phase === 'error' || state.phase === 'final') {
      clearStoredPlayerSession()
      setStoredSession(null)
      setIsReconnecting(false)
      dispatch({ type: 'RESET' })
      return
    }

    if (state.phase !== 'idle') {
      setIsReconnecting(false)
    }
  }, [dispatch, isReconnecting, state.phase])

  if (isReconnecting) {
    return <ReconnectingPanel nickname={storedSession?.nickname ?? null} />
  }

  // Anything other than the initial `idle` phase means we have either
  // started joining (`PLAYER_JOINED` → `lobby`) or the server has already
  // emitted a question / results / final event. In every case the right
  // surface is the in-game view; the only screen unique to this route is
  // the entry form itself.
  if (state.phase === 'idle') {
    return <JoinForm />
  }

  return <PlayerGameView />
}

function ReconnectingPanel({ nickname }: { nickname: string | null }) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-gradient-to-bl from-brand-900 via-brand-800 to-brand-900 text-white">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.18),transparent_60%)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -top-24 -end-24 h-72 w-72 rounded-full bg-brand-400/25 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-24 -start-24 h-80 w-80 rounded-full bg-brand-300/15 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-lg flex-col items-center justify-center ps-4 pe-4 sm:ps-6 sm:pe-6 py-10 text-center">
        <div className="w-full rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl ps-6 pe-6 sm:ps-8 sm:pe-8 py-10">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand-100" aria-hidden="true" />
          <p className="mt-5 text-2xl font-display font-bold text-white">جارٍ إعادة الاتصال...</p>
          {nickname ? <p className="mt-3 text-sm text-brand-100/75">{nickname}</p> : null}
        </div>
      </div>
    </main>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Join form
// ─────────────────────────────────────────────────────────────────────────────

const PIN_LENGTH = 6
const NICKNAME_MAX = 20
const PIN_PATTERN = /^\d{6}$/

/**
 * Normalise a raw PIN string to clean digits: strip everything that isn't
 * a digit (an over-eager mobile keyboard, or a paste from a chat app that
 * wrapped the number in spaces / hyphens) and clamp to PIN_LENGTH. Shared
 * by the change handler and the URL-seeded initial value so both apply the
 * exact same sanitisation.
 */
function sanitizePin(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, PIN_LENGTH)
}

function JoinForm() {
  const { t } = useTranslation('game')
  const { invoke } = useGameState()
  const { pin: searchPin } = Route.useSearch()

  const [pin, setPin] = useState(() => sanitizePin(searchPin))
  const [nickname, setNickname] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const [nicknameError, setNicknameError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Auto-focus the nickname input once the PIN is filled. A ref + manual
  // focus is friendlier than `tabIndex` chaining for mobile users who tap
  // through the numeric keypad and would otherwise have to reach up and
  // hit the next field with their thumb.
  const nicknameRef = useRef<HTMLInputElement | null>(null)
  // Whether the PIN arrived prefilled & valid from the URL. Captured once
  // on first render so it reflects the seeded value, not later edits.
  const pinPrefilledRef = useRef(PIN_PATTERN.test(pin))

  // When the PIN is already complete on mount (deep-linked from the host's
  // join link), the player only needs to type their name — send focus
  // straight to the nickname field instead of the prefilled PIN input.
  useEffect(() => {
    if (pinPrefilledRef.current) {
      nicknameRef.current?.focus()
    }
  }, [])

  const handlePinChange = (raw: string) => {
    const digits = sanitizePin(raw)
    setPin(digits)
    if (pinError) setPinError(null)
    if (digits.length === PIN_LENGTH) {
      // Hand focus over so the player keeps moving without re-targeting.
      nicknameRef.current?.focus()
    }
  }

  const handleNicknameChange = (value: string) => {
    setNickname(value.slice(0, NICKNAME_MAX))
    if (nicknameError) setNicknameError(null)
  }

  const validate = useCallback(() => {
    let ok = true
    if (!PIN_PATTERN.test(pin)) {
      setPinError(t('pinDigitsRequired'))
      ok = false
    }
    const trimmed = nickname.trim()
    if (trimmed.length === 0) {
      setNicknameError(t('nicknameRequired'))
      ok = false
    } else if (trimmed.length > NICKNAME_MAX) {
      setNicknameError(t('nicknameTooLong'))
      ok = false
    }
    return ok
  }, [pin, nickname, t])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitError(null)
    if (!validate()) return
    setIsSubmitting(true)
    try {
      await invoke('JoinAsPlayer', pin, nickname.trim())
      // No navigation: once the server emits `playerJoined`, the
      // reducer flips `state.phase` to `'lobby'` and `<PlayInner />`
      // re-renders into the `<PlayerGameView />` branch.
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('connectionError'))
      setIsSubmitting(false)
    }
    // We deliberately leave `isSubmitting` true on success so the form
    // stays locked between submission and the phase transition — the
    // provider will swap the whole subtree once `state.phase` updates,
    // so there is no UI risk of a stale spinner.
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-gradient-to-bl from-brand-900 via-brand-800 to-brand-900 text-white">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.18),transparent_60%)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -top-24 -end-24 h-72 w-72 rounded-full bg-brand-400/25 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-24 -start-24 h-80 w-80 rounded-full bg-brand-300/15 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-lg flex-col items-center justify-center ps-4 pe-4 sm:ps-6 sm:pe-6 py-10">
        <header className="text-center">
          <span
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 ps-4 pe-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-100 backdrop-blur-sm"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            <span>ArabQuiz</span>
          </span>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
            {t('playerJoinTitle')}
          </h1>
          <p className="mt-3 text-base text-brand-100/80 leading-relaxed">
            {t('playerJoinSubtitle')}
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="mt-8 w-full rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl ps-6 pe-6 sm:ps-8 sm:pe-8 py-7 sm:py-8"
        >
          <div className="space-y-5">
            <div>
              <label
                htmlFor="pin"
                className="block text-sm font-semibold text-brand-100/90 mb-2"
              >
                {t('joinPinLabel')}
              </label>
              <input
                id="pin"
                name="pin"
                type="text"
                inputMode="numeric"
                pattern="\d*"
                autoComplete="off"
                autoFocus={!pinPrefilledRef.current}
                dir="ltr"
                aria-invalid={pinError ? true : undefined}
                aria-describedby={pinError ? 'pin-error' : undefined}
                value={pin}
                onChange={(e) => handlePinChange(e.target.value)}
                placeholder={t('pinPlaceholder')}
                maxLength={PIN_LENGTH}
                className="glass-input block w-full rounded-2xl bg-brand-900/80 border border-white/15 backdrop-blur-sm ps-4 pe-4 py-4 text-center font-display text-4xl sm:text-5xl font-bold tabular-nums tracking-[0.4em] text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-brand-300 focus:bg-brand-800 appearance-none transition-colors"
              />
              {pinError ? (
                <p
                  id="pin-error"
                  className="mt-2 text-xs text-red-200"
                  role="alert"
                >
                  {pinError}
                </p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="nickname"
                className="block text-sm font-semibold text-brand-100/90 mb-2"
              >
                {t('enterNickname')}
              </label>
              <input
                id="nickname"
                name="nickname"
                type="text"
                autoComplete="nickname"
                ref={nicknameRef}
                aria-invalid={nicknameError ? true : undefined}
                aria-describedby={nicknameError ? 'nickname-error' : undefined}
                value={nickname}
                onChange={(e) => handleNicknameChange(e.target.value)}
                placeholder={t('nicknamePlaceholder')}
                maxLength={NICKNAME_MAX}
                className="glass-input block w-full rounded-2xl bg-brand-900/80 border border-white/15 backdrop-blur-sm ps-4 pe-4 py-4 text-center font-display text-2xl font-bold text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-brand-300 focus:bg-brand-800 appearance-none transition-colors"
              />
              {nicknameError ? (
                <p
                  id="nickname-error"
                  className="mt-2 text-xs text-red-200"
                  role="alert"
                >
                  {nicknameError}
                </p>
              ) : null}
            </div>

            {submitError ? (
              <div
                className="rounded-xl border border-red-300/40 bg-red-500/15 text-red-100 ps-4 pe-4 py-3 text-sm"
                role="alert"
              >
                {submitError}
              </div>
            ) : null}

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="mt-2 h-14 w-full gap-2 bg-white text-brand-900 text-lg font-bold shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.01] active:scale-100 disabled:bg-white/20 disabled:text-white/50 disabled:shadow-none"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  <span>{t('joining')}</span>
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" aria-hidden="true" />
                  <span>{t('join')}</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </main>
  )
}
